import {
  Box,
  CircularProgress,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useAccountQuery } from "features/auth/authApi";
import { useBalancesQuery } from "features/finance/financeApi";
import React, { useEffect, useMemo } from "react";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import ArrowLeftIcon from "@mui/icons-material/ArrowLeft";
import CurrencyIcon from "components/currency-icon";
import { formatNumber } from "utils/formatNumber";
import numeral from "numeral";
import { useTranslation } from "react-i18next";

type Props = {
  isSmall?: boolean;
};

type Fiat = "usd" | "eur" | "cad" | "gbp" | "chf" | "aud";

const BalanceInfo: React.FC<Props> = ({ isSmall }) => {
  const { t }: { t: (key: string, opts?: any) => string } = useTranslation();
  const [selectedAsset, setSelectedAsset] = React.useState<string>("");
  const { data: balances, isLoading } = useBalancesQuery(undefined, {
    pollingInterval: 60000,
  });

  const { data: account } = useAccountQuery(undefined, {
    pollingInterval: 60000,
  });
  const default_currency: Fiat =
    (account?.user.currency as Fiat) ||
    (account?.user.kycProgram.default_currency as Fiat);

  useEffect(() => {
    if (!selectedAsset && account?.user.kycProgram.supported_cryptos[0]) {
      setSelectedAsset(account.user.kycProgram.supported_cryptos[0]);
    }
  }, [account, selectedAsset]);

  const rate = useMemo(() => {
    if (default_currency) {
      const selectedRate =
        balances?.rates[selectedAsset as keyof typeof balances.rates];

      const rate =
        selectedRate && selectedRate[default_currency as Fiat]
          ? Number(selectedRate[default_currency as Fiat])
          : "0";
      return rate;
    }
    return 0;
  }, [balances, default_currency, selectedAsset]);

  const handlePrev = () => {
    if (account?.user.kycProgram.supported_cryptos) {
      const index =
        account.user.kycProgram.supported_cryptos.indexOf(selectedAsset);
      if (index > 0) {
        setSelectedAsset(account.user.kycProgram.supported_cryptos[index - 1]);
      }
    }
  };

  const nextItem = () => {
    if (account) {
      const index =
        account.user.kycProgram.supported_cryptos.indexOf(selectedAsset);
      return account.user.kycProgram.supported_cryptos[index + 1];
    }
  };

  const prevItem = () => {
    if (account) {
      const index =
        account.user.kycProgram.supported_cryptos.indexOf(selectedAsset);
      return account.user.kycProgram.supported_cryptos[index - 1];
    }
  };

  const handleNext = () => {
    if (account?.user.kycProgram.supported_cryptos) {
      const index =
        account.user.kycProgram.supported_cryptos.indexOf(selectedAsset);
      if (index < account.user.kycProgram.supported_cryptos.length - 1) {
        setSelectedAsset(account.user.kycProgram.supported_cryptos[index + 1]);
      }
    }
  };

  const getAvailableBalance = (asset: string) => {
    const selectedSummary = balances?.newArchBalances.summary.find(
      (s) => s.asset.toString() === asset
    );
    if (!selectedSummary) {
      return formatNumber(0);
    }

    return formatNumber(selectedSummary.available);
  };

  const getTotalBalance = (asset: string) => {
    const selectedSummary = balances?.newArchBalances.summary.find(
      (s) => s.asset.toString() === asset
    );
    if (!selectedSummary) {
      return formatNumber(0);
    }

    return formatNumber(selectedSummary.total);
  };

  const getTotalFiatBalance = (asset: string, currency: string | undefined) => {
    const selectedSummary = balances?.newArchBalances.summary.find(
      (s) => s.asset.toString() === asset
    );
    if (!selectedSummary) {
      return formatNumber(0);
    }

    return numeral(
      currency === "usd" ? selectedSummary.totalUsd : selectedSummary.totalEur
    ).format("0,00");
  };

  const getAvailableFiatBalance = (
    asset: string,
    currency: string | undefined
  ) => {
    const selectedSummary = balances?.newArchBalances.summary.find(
      (s) => s.asset.toString() === asset
    );
    if (!selectedSummary) {
      return formatNumber(0);
    }

    return numeral(
      currency === "usd"
        ? selectedSummary.availableUsd
        : selectedSummary.availableEur
    ).format("0,00");
  };

  return (
    <Stack
      direction={isSmall ? "column" : "row"}
      spacing={2}
      divider={<Divider orientation="vertical" flexItem />}
    >
      <Box justifyContent={"space-between"} display={"flex"}>
        <Tooltip title={t("balance.previous")}>
          <IconButton
            sx={{ alignSelf: "center" }}
            color="primary"
            onClick={handlePrev}
            disabled={!prevItem()}
          >
            <ArrowLeftIcon />
          </IconButton>
        </Tooltip>
        {isSmall ? (
          <Tooltip title={t("balance.next")}>
            <IconButton
              color="primary"
              onClick={handleNext}
              disabled={!nextItem()}
            >
              <ArrowRightIcon />
            </IconButton>
          </Tooltip>
        ) : null}
      </Box>

      <Stack spacing={0.2}>
        <Typography fontWeight={600} fontSize={12}>
          {t("balance.available")}
        </Typography>
        <Typography
          fontWeight={400}
          fontSize={13}
          alignItems="center"
          display="flex"
          gap={0.5}
        >
          <CurrencyIcon currency={selectedAsset} size={20} />
          {isLoading ? (
            <CircularProgress size={14} />
          ) : (
            getAvailableBalance(selectedAsset)
          )}
        </Typography>
        <Typography
          fontWeight={400}
          fontSize={13}
          alignItems="center"
          display="flex"
          gap={0.5}
        >
          <CurrencyIcon currency={default_currency} size={20} />
          {isLoading ? (
            <CircularProgress size={14} />
          ) : (
            default_currency &&
            rate &&
            getAvailableFiatBalance(selectedAsset, default_currency)
          )}
        </Typography>
      </Stack>

      {/* Total */}
      <Stack spacing={0.2}>
        <Typography fontWeight={600} fontSize={12}>
          {t("balance.total_balance")}
        </Typography>
        <Typography
          fontWeight={400}
          fontSize={13}
          alignItems="center"
          display="flex"
          gap={0.5}
        >
          <CurrencyIcon currency={selectedAsset} size={20} />
          {isLoading ? (
            <CircularProgress size={14} />
          ) : (
            getTotalBalance(selectedAsset)
          )}
        </Typography>
        <Typography
          fontWeight={400}
          fontSize={13}
          alignItems="center"
          display="flex"
          gap={0.5}
        >
          <CurrencyIcon currency={default_currency} size={20} />
          {isLoading ? (
            <CircularProgress size={14} />
          ) : (
            default_currency &&
            rate &&
            getTotalFiatBalance(selectedAsset, default_currency)
          )}
        </Typography>
      </Stack>

      {/* Rate */}
      <Stack spacing={0.2}>
        <Typography fontWeight={600} fontSize={12}>
          {t("balance.rate")}
        </Typography>
        <Typography
          fontWeight={400}
          fontSize={13}
          alignItems="center"
          display="flex"
          gap={0.5}
          noWrap
        >
          <CurrencyIcon currency={selectedAsset} size={20} />1 ≈{" "}
          {isLoading ? (
            <CircularProgress size={14} />
          ) : (
            numeral(rate).format("0,00.0")
          )}
          <CurrencyIcon currency={default_currency} size={20} />
        </Typography>
      </Stack>

      {!isSmall && (
        <Box display={"flex"} justifyContent={"center"}>
          <Tooltip title={t("balance.next")}>
            <IconButton
              sx={{ alignSelf: "center" }}
              onClick={handleNext}
              disabled={!nextItem()}
              color="primary"
            >
              <ArrowRightIcon />
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </Stack>
  );
};

export default BalanceInfo;
